export type FAQCategory = 'General' | 'Pricing' | 'Technical' | 'Account' | 'Features' | 'Billing';

export interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: FAQCategory;
  tags: string[];
}

export const faqs: FAQ[] = [
  // General FAQs
  {
    id: '1',
    question: 'How accurate is Wisprs?',
    answer:
      'Wisprs uses industry-leading speech recognition: self-hosted Whisper-based models on the free tier and ElevenLabs Scribe on paid plans. Accuracy is typically very high on clear speech and varies by language, accent, background noise, and audio quality. Published provider benchmarks use specific test sets; real files may differ.',
    category: 'General',
    tags: ['accuracy'],
  },
  {
    id: '2',
    question: 'What file formats do you support?',
    answer:
      'We support all common audio and video formats including AAC, FLAC, M4A, MP3, MP4, MPEG, MPGA, OGG, WAV, WEBM, and more.',
    category: 'General',
    tags: ['formats', 'upload'],
  },
  {
    id: '3',
    question: 'How long does transcription take?',
    answer:
      'Most transcriptions complete in under 5 minutes. A 60-minute audio file typically takes about 3-4 minutes to process.',
    category: 'General',
    tags: ['accuracy', 'features'],
  },
  {
    id: '4',
    question: 'Is my data secure?',
    answer:
      'Yes. We never use your data to train AI models. All files are stored securely and you retain 100% ownership of your content.',
    category: 'General',
    tags: ['security'],
  },
  {
    id: '5',
    question: 'Can I cancel anytime?',
    answer:
      "Yes, you can cancel your subscription at any time. You'll continue to have access until the end of your billing period.",
    category: 'General',
    tags: ['subscription'],
  },
  {
    id: '6',
    question: 'Do you support multiple languages?',
    answer:
      'Yes! Wisprs supports 100+ languages with automatic language detection. You can also translate your transcriptions instantly.',
    category: 'General',
    tags: ['features'],
  },
  {
    id: '7',
    question: 'Can I export transcripts in different formats?',
    answer:
      'Absolutely. You can export your transcripts in multiple formats including TXT, SRT, VTT, and DOCX with perfect formatting.',
    category: 'General',
    tags: ['formats'],
  },
  {
    id: '8',
    question: 'What happens if I exceed my plan limits?',
    answer:
      "We'll prompt you to upgrade or add minutes before you hit the limit. You can change your plan or buy extra minutes anytime.",
    category: 'General',
    tags: ['pricing', 'subscription'],
  },
  {
    id: '9',
    question: 'How do I get started?',
    answer:
      "Simply sign up for a free account, upload your first audio or video file, and we'll transcribe it for you. No credit card required for the free tier.",
    category: 'General',
    tags: ['account', 'pricing'],
  },
  {
    id: '10',
    question: 'Can I transcribe phone calls?',
    answer:
      'Yes! You can upload recordings of phone calls in any supported format. We recommend clear audio quality for best results.',
    category: 'General',
    tags: ['upload'],
  },

  // Pricing FAQs
  {
    id: '11',
    question: 'What is included in the free plan?',
    answer:
      'The free plan includes 30 minutes of transcription per month, basic export formats, and access to all core features. Perfect for trying out Wisprs.',
    category: 'Pricing',
    tags: ['pricing'],
  },
  {
    id: '12',
    question: 'How does billing work?',
    answer:
      "Billing is monthly or annual depending on your plan. You're charged at the beginning of each billing cycle. Annual plans offer significant savings.",
    category: 'Pricing',
    tags: ['pricing', 'subscription'],
  },
  {
    id: '13',
    question: 'Can I change my plan later?',
    answer:
      "Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately, and we'll prorate any charges or credits.",
    category: 'Pricing',
    tags: ['pricing', 'subscription'],
  },
  {
    id: '14',
    question: 'Do you offer refunds?',
    answer:
      "We offer a 30-day money-back guarantee. If you're not satisfied, contact us within 30 days for a full refund.",
    category: 'Pricing',
    tags: ['pricing', 'support'],
  },
  {
    id: '15',
    question: 'Are there any hidden fees?',
    answer:
      'No hidden fees. The price you see is what you pay. All features listed in your plan are included at no extra cost.',
    category: 'Pricing',
    tags: ['pricing'],
  },
  {
    id: '16',
    question: 'Do you offer student discounts?',
    answer:
      'Yes! Students with a valid .edu email address can get 50% off any paid plan. Contact support to verify your student status.',
    category: 'Pricing',
    tags: ['pricing', 'support'],
  },
  {
    id: '17',
    question: 'What payment methods do you accept?',
    answer:
      'We accept all major credit cards, debit cards, and PayPal. Enterprise customers can also pay via invoice.',
    category: 'Pricing',
    tags: ['pricing'],
  },
  {
    id: '18',
    question: 'Can I purchase additional minutes?',
    answer:
      'Yes, you can purchase additional transcription minutes as add-ons. These roll over month-to-month and never expire.',
    category: 'Pricing',
    tags: ['pricing'],
  },
  {
    id: '19',
    question: 'Is there a limit on file size?',
    answer:
      'Free plans have a 100MB file size limit. Paid plans support files up to 2GB. For larger files, contact us for enterprise solutions.',
    category: 'Pricing',
    tags: ['pricing', 'upload'],
  },
  {
    id: '20',
    question: 'Do you offer team or enterprise plans?',
    answer:
      'Yes! We offer custom enterprise plans with volume discounts, dedicated support, and advanced features. Contact our sales team for details.',
    category: 'Pricing',
    tags: ['pricing', 'support'],
  },

  // Technical FAQs
  {
    id: '21',
    question: 'What is the maximum audio quality supported?',
    answer:
      'We support audio files up to 48kHz sample rate and 24-bit depth. Higher quality audio generally results in better transcription accuracy.',
    category: 'Technical',
    tags: ['accuracy', 'features'],
  },
  {
    id: '22',
    question: 'Can I transcribe live audio streams?',
    answer:
      'Currently, we support file uploads only. Live streaming transcription is coming soon and will be available on Pro and Enterprise plans.',
    category: 'Technical',
    tags: ['features', 'upload'],
  },
  {
    id: '23',
    question: 'How does speaker recognition work?',
    answer:
      'Our advanced AI identifies different speakers in your audio and labels them as "Speaker 1", "Speaker 2", etc. This works best with clear audio and distinct voices.',
    category: 'Technical',
    tags: ['features'],
  },
  {
    id: '24',
    question: 'What if my audio has background noise?',
    answer:
      'Wisprs is designed to handle background noise and still provide accurate transcriptions. However, clearer audio will always produce better results.',
    category: 'Technical',
    tags: ['accuracy'],
  },
  {
    id: '25',
    question: "Can I edit transcripts after they're generated?",
    answer:
      'Yes! You can edit any transcript directly in the dashboard. All edits are saved automatically and can be re-exported in any format.',
    category: 'Technical',
    tags: ['features', 'account'],
  },
  {
    id: '26',
    question: 'Do you support timestamps?',
    answer:
      'Yes, all transcripts include timestamps. You can export with timestamps in SRT or VTT formats for video subtitles.',
    category: 'Technical',
    tags: ['features', 'formats'],
  },
  {
    id: '27',
    question: 'What is the API rate limit?',
    answer:
      'API rate limits vary by plan. Free: 10 requests/hour, Pro: 100 requests/hour, Enterprise: Custom limits. Check your plan details for specifics.',
    category: 'Technical',
    tags: ['api', 'pricing'],
  },
  {
    id: '28',
    question: 'Can I integrate Wisprs with my application?',
    answer:
      'Yes! We offer a comprehensive REST API and webhooks. Documentation is available in your dashboard for Pro and Enterprise users.',
    category: 'Technical',
    tags: ['api'],
  },
  {
    id: '29',
    question: 'How do you handle different accents?',
    answer:
      'Our transcription engines (Whisper-based and ElevenLabs Scribe) handle diverse accents and dialects. They support regional accents, non-native speakers, and various speaking styles effectively.',
    category: 'Technical',
    tags: ['accuracy'],
  },
  {
    id: '30',
    question: 'What happens if transcription fails?',
    answer:
      "If a transcription fails, you'll be notified immediately. The file won't count against your quota, and you can retry or contact support for help.",
    category: 'Technical',
    tags: ['support'],
  },

  // Account FAQs
  {
    id: '31',
    question: 'How do I reset my password?',
    answer:
      'Click "Forgot Password" on the login page, or go to Account Settings > Security. You\'ll receive an email with reset instructions.',
    category: 'Account',
    tags: ['account', 'security'],
  },
  {
    id: '32',
    question: 'Can I have multiple users on one account?',
    answer:
      'Team and Enterprise plans support multiple users with role-based access. Individual plans are single-user only.',
    category: 'Account',
    tags: ['account', 'pricing'],
  },
  {
    id: '33',
    question: 'How do I delete my account?',
    answer:
      'Go to Account Settings > Data & Privacy > Request account deletion. You will type a confirmation phrase, review the scope, and receive a support ticket so we can verify and process the request safely. Your account stays available until the review is complete.',
    category: 'Account',
    tags: ['account', 'security'],
  },
  {
    id: '34',
    question: 'Can I change my email address?',
    answer:
      "Yes, you can update your email in Account Settings. You'll need to verify the new email address before it becomes active.",
    category: 'Account',
    tags: ['account'],
  },
  {
    id: '35',
    question: 'How long are transcripts stored?',
    answer:
      'Transcripts remain available while your account is active unless you delete them or your plan terms say otherwise. Export important work anytime. Backup and legal retention can mean limited copies remain after deletion.',
    category: 'Account',
    tags: ['account', 'pricing'],
  },
  {
    id: '36',
    question: 'Can I share transcripts with others?',
    answer:
      'Yes! You can generate shareable links with password protection, or export and share files directly. Sharing settings are in each transcript.',
    category: 'Account',
    tags: ['account', 'features'],
  },
  {
    id: '37',
    question: 'Do you offer two-factor authentication?',
    answer:
      'Yes, 2FA is available for all accounts. Enable it in Account Settings > Security for an extra layer of protection.',
    category: 'Account',
    tags: ['account', 'security'],
  },
  {
    id: '38',
    question: 'How do I update my billing information?',
    answer:
      'Go to Account Settings > Billing. You can update your payment method, billing address, and view invoices there.',
    category: 'Account',
    tags: ['account', 'pricing'],
  },
  {
    id: '39',
    question: 'Can I download all my transcripts at once?',
    answer:
      'Yes! Pro and Enterprise users can bulk download all transcripts. Free users can download individually or upgrade for bulk access.',
    category: 'Account',
    tags: ['account', 'formats'],
  },
  {
    id: '40',
    question: 'How do I contact support?',
    answer:
      'You can reach us via email at tosh@getwisprs.com, through the in-app chat, or submit a ticket in your dashboard. We typically respond within 24 hours.',
    category: 'Account',
    tags: ['support'],
  },

  // Features FAQs
  {
    id: '41',
    question: 'Do you support video transcription?',
    answer:
      'Yes! We transcribe both audio and video files. Video files are processed to extract audio, then transcribed with timestamps.',
    category: 'Features',
    tags: ['features', 'upload'],
  },
  {
    id: '42',
    question: 'Can I get word-level timestamps?',
    answer:
      'Yes, word-level timestamps are available on Pro and Enterprise plans. Perfect for precise video editing and subtitle creation.',
    category: 'Features',
    tags: ['features', 'formats'],
  },
  {
    id: '43',
    question: 'Do you support custom vocabulary?',
    answer:
      'Enterprise plans support custom vocabulary and terminology. This helps improve accuracy for industry-specific terms and names.',
    category: 'Features',
    tags: ['features', 'accuracy'],
  },
  {
    id: '44',
    question: 'Can I transcribe multiple files at once?',
    answer:
      'Yes! You can upload and transcribe multiple files simultaneously. Batch processing is available on all plans, with higher limits on paid tiers.',
    category: 'Features',
    tags: ['features', 'upload'],
  },
  {
    id: '45',
    question: 'Do you offer transcription templates?',
    answer:
      'Yes, you can create custom templates for formatting, speaker labels, and styling. Templates save time for recurring transcription needs.',
    category: 'Features',
    tags: ['features', 'formats'],
  },
  {
    id: '46',
    question: 'Can I get transcripts in real-time?',
    answer:
      'Real-time transcription is available for Enterprise customers via our API. Standard file uploads process in minutes, not seconds.',
    category: 'Features',
    tags: ['features', 'api'],
  },
  {
    id: '47',
    question: 'Do you support punctuation and capitalization?',
    answer:
      'Yes! All transcripts include proper punctuation, capitalization, and formatting. You can adjust these settings in your preferences.',
    category: 'Features',
    tags: ['features', 'formats'],
  },
  {
    id: '48',
    question: 'Can I search through my transcripts?',
    answer:
      'Yes! The dashboard includes a powerful search feature to find specific words, phrases, or topics across all your transcripts.',
    category: 'Features',
    tags: ['features', 'account'],
  },
  {
    id: '49',
    question: 'Do you support automatic language detection?',
    answer:
      'Yes, Wisprs automatically detects the language in your audio. You can also manually specify the language if needed.',
    category: 'Features',
    tags: ['features'],
  },
  {
    id: '50',
    question: 'Can I get transcripts with speaker diarization?',
    answer:
      'Yes! Speaker diarization (identifying who said what) is included in all plans. It works best with clear audio and distinct voices.',
    category: 'Features',
    tags: ['features'],
  },

  // Billing FAQs
  {
    id: '51',
    question: 'When will I be charged?',
    answer:
      "You're charged at the beginning of each billing cycle. For monthly plans, that's the same date each month. Annual plans charge once per year.",
    category: 'Billing',
    tags: ['pricing', 'subscription'],
  },
  {
    id: '52',
    question: 'What happens if my payment fails?',
    answer:
      "We'll notify you immediately and retry the payment. If it fails after multiple attempts, your account will be paused until payment is updated.",
    category: 'Billing',
    tags: ['pricing', 'support'],
  },
  {
    id: '53',
    question: 'Can I get an invoice for my purchase?',
    answer:
      'Yes! All invoices are available in your Account Settings > Billing section. You can download PDF invoices for any payment.',
    category: 'Billing',
    tags: ['pricing', 'account'],
  },
  {
    id: '54',
    question: 'Do unused minutes roll over?',
    answer:
      'Unused transcription minutes roll over month-to-month on paid plans. Free plan minutes reset each month and do not roll over.',
    category: 'Billing',
    tags: ['pricing', 'subscription'],
  },
  {
    id: '55',
    question: 'How do I update my credit card?',
    answer:
      'Go to Account Settings > Billing > Payment Method. You can update your card information there. Changes take effect immediately.',
    category: 'Billing',
    tags: ['pricing', 'account'],
  },
];

export const homeFAQs = faqs.filter((faq) => faq.category === 'General').slice(0, 8);

export const categories: FAQCategory[] = [
  'General',
  'Pricing',
  'Technical',
  'Account',
  'Features',
  'Billing',
];

// Top 10 most useful tags
export const allTags: string[] = [
  'pricing',
  'security',
  'formats',
  'api',
  'account',
  'features',
  'support',
  'upload',
  'subscription',
  'accuracy',
];
